/**
 * 目录结构全屏编辑器
 * 支持：拖拽排序、节点增删改、编辑标题/描述/字数范围
 */
import React, { useState, useCallback, useRef } from 'react';
import { OutlineItem, OutlineData } from '../types';
import {
  PlusIcon, TrashIcon, PencilSquareIcon,
  DocumentTextIcon, CheckCircleIcon,
  XMarkIcon, ArrowsPointingOutIcon,
  ChevronRightIcon, ChevronDownIcon,
  ArrowPathIcon, SparklesIcon,
} from '@heroicons/react/24/outline';
import { projectApi, getErrorMessage } from '../services/api';
import ConfirmDialog from './ConfirmDialog';

interface OutlineEditorDialogProps {
  open: boolean;
  outlineData: OutlineData | null;
  projectId?: number;
  section?: string;
  onClose: () => void;
  onOutlineUpdated?: (outlineData: OutlineData) => void;
  readOnly?: boolean;
}

// ============================================================
// 树操作工具函数（与 ContentEdit 中的逻辑等价，独立实现避免依赖）
// ============================================================

// 从树中移除指定节点
function removeItemFromTree(items: OutlineItem[], targetId: string): { items: OutlineItem[]; removed: OutlineItem | null } {
  const result: OutlineItem[] = [];
  let removed: OutlineItem | null = null;
  for (const item of items) {
    if (item.id === targetId) {
      removed = item;
      continue;
    }
    if (item.children && item.children.length > 0) {
      const res = removeItemFromTree(item.children, targetId);
      if (res.removed) {
        removed = res.removed;
        result.push({ ...item, children: res.items.length > 0 ? res.items : undefined });
      } else {
        result.push(item);
      }
    } else {
      result.push(item);
    }
  }
  return { items: result, removed };
}

// 在树中插入节点，未找到目标时返回原数组引用
function insertItemToTree(
  items: OutlineItem[],
  itemToInsert: OutlineItem,
  targetId: string,
  position: 'before' | 'after' | 'inside'
): OutlineItem[] {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const left = items.slice(0, i);
    const right = items.slice(i + 1);

    if (item.id === targetId) {
      if (position === 'before') {
        return [...left, itemToInsert, item, ...right];
      } else if (position === 'after') {
        return [...left, item, itemToInsert, ...right];
      } else {
        const newChildren = item.children ? [...item.children, itemToInsert] : [itemToInsert];
        return [...left, { ...item, children: newChildren }, ...right];
      }
    }

    if (item.children && item.children.length > 0) {
      const newChildren = insertItemToTree(item.children, itemToInsert, targetId, position);
      if (newChildren !== item.children) {
        return [...left, { ...item, children: newChildren }, ...right];
      }
    }
  }
  return items;
}

// 深度优先重新编号
function renumberTree(items: OutlineItem[], parentPrefix: string = ''): OutlineItem[] {
  return items.map((item, index) => {
    const newId = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
    return {
      ...item,
      id: newId,
      children: item.children && item.children.length > 0
        ? renumberTree(item.children, newId)
        : undefined,
    };
  });
}

// 判断 candidateId 是否是 ancestorId 的后代
function isDescendantOf(items: OutlineItem[], candidateId: string, ancestorId: string): boolean {
  const searchInSubtree = (list: OutlineItem[], target: string): boolean => {
    for (const item of list) {
      if (item.id === target) return true;
      if (item.children && item.children.length > 0) {
        if (searchInSubtree(item.children, target)) return true;
      }
    }
    return false;
  };
  const findAndCheck = (list: OutlineItem[], ancId: string, candId: string): boolean => {
    for (const item of list) {
      if (item.id === ancId) {
        if (item.children) return searchInSubtree(item.children, candId);
        return false;
      }
      if (item.children && item.children.length > 0) {
        if (findAndCheck(item.children, ancId, candId)) return true;
      }
    }
    return false;
  };
  return findAndCheck(items, ancestorId, candidateId);
}

// 扁平化节点（用于保存到后端）
function flattenNodes(items: OutlineItem[], acc: any[] = [], parentId: string | null = null, section: string = 'tech'): any[] {
  for (const item of items) {
    acc.push({
      id: item.id,
      parent_node_id: parentId,
      title: item.title,
      description: item.description || '',
      word_count_range: item.word_count_range || '',
      source_requirement_id: item.source_requirement_id || undefined,
      source_requirement_title: item.source_requirement_title || undefined,
      section,
    });
    if (item.children && item.children.length > 0) {
      flattenNodes(item.children, acc, item.id, section);
    }
  }
  return acc;
}

// 生成临时 ID
let tempIdCounter = 0;
function generateTempId(): string {
  return `__new_${Date.now()}_${++tempIdCounter}`;
}

const OutlineEditorDialog: React.FC<OutlineEditorDialogProps> = ({
  open,
  outlineData,
  projectId,
  section = 'tech',
  onClose,
  onOutlineUpdated,
  readOnly = false,
}) => {
  // 本地副本
  const [localOutline, setLocalOutline] = useState<OutlineItem[]>([]);
  const [initialized, setInitialized] = useState(false);

  // 正在编辑标题的节点 ID
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editTitleText, setEditTitleText] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // 正在编辑 meta（描述/字数范围）的节点
  const [editingMetaId, setEditingMetaId] = useState<string | null>(null);
  const [editingMetaTitle, setEditingMetaTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editWordCountRange, setEditWordCountRange] = useState('');
  const [aiGeneratingDesc, setAiGeneratingDesc] = useState(false);

  // 删除确认
  const [deleteConfirm, setDeleteConfirm] = useState<{ nodeId: string; nodeTitle: string; hasChildren: boolean; hasContent: boolean } | null>(null);

  // 拖拽状态
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: 'before' | 'inside' | 'after' } | null>(null);
  const dragNodeRef = useRef<OutlineItem | null>(null);

  // 保存状态
  const [saving, setSaving] = useState(false);

  // 是否显示所有章节描述
  const [showDescriptions, setShowDescriptions] = useState(false);

  // 折叠/展开：记录已折叠的父节点 ID
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  // 当弹窗打开时初始化本地副本
  React.useEffect(() => {
    if (open && outlineData) {
      setLocalOutline(JSON.parse(JSON.stringify(outlineData.outline)));
      setInitialized(true);
    }
    if (!open) {
      setInitialized(false);
      setEditingTitleId(null);
      setEditingMetaId(null);
      setDeleteConfirm(null);
      setCollapsedIds(new Set());
    }
  }, [open, outlineData]);

  // ==================== 节点 CRUD ====================

  const updateNodeInTree = useCallback((
    items: OutlineItem[],
    targetId: string,
    updater: (node: OutlineItem) => OutlineItem
  ): OutlineItem[] => {
    return items.map(item => {
      if (item.id === targetId) {
        return updater(item);
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: updateNodeInTree(item.children, targetId, updater) };
      }
      return item;
    });
  }, []);

  // 添加子节点
  const handleAddChild = useCallback((parentId: string) => {
    setLocalOutline(prev => {
      const newChild: OutlineItem = {
        id: generateTempId(),
        title: '新章节',
        description: '',
        word_count_range: '',
      };
      return updateNodeInTree(prev, parentId, (node) => ({
        ...node,
        children: node.children ? [...node.children, newChild] : [newChild],
      }));
    });
  }, [updateNodeInTree]);

  // 添加同级节点
  const handleAddSibling = useCallback((siblingId: string) => {
    setLocalOutline(prev => {
      const newSibling: OutlineItem = {
        id: generateTempId(),
        title: '新章节',
        description: '',
        word_count_range: '',
      };
      return insertItemToTree(prev, newSibling, siblingId, 'after');
    });
  }, []);

  // 删除节点
  const handleDeleteClick = useCallback((node: OutlineItem) => {
    const hasChildren = !!(node.children && node.children.length > 0);
    const hasContent = !!node.content;
    setDeleteConfirm({
      nodeId: node.id,
      nodeTitle: node.title,
      hasChildren,
      hasContent,
    });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteConfirm) return;
    setLocalOutline(prev => removeItemFromTree(prev, deleteConfirm.nodeId).items);
    setDeleteConfirm(null);
  }, [deleteConfirm]);

  // 开始编辑标题
  const startEditTitle = useCallback((node: OutlineItem) => {
    setEditingTitleId(node.id);
    setEditTitleText(node.title);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  }, []);

  // 保存标题
  const saveEditTitle = useCallback(() => {
    if (!editingTitleId) return;
    const trimmed = editTitleText.trim();
    if (!trimmed) return;
    setLocalOutline(prev => updateNodeInTree(prev, editingTitleId, (node) => ({
      ...node,
      title: trimmed,
    })));
    setEditingTitleId(null);
  }, [editingTitleId, editTitleText, updateNodeInTree]);

  // 取消编辑标题
  const cancelEditTitle = useCallback(() => {
    setEditingTitleId(null);
  }, []);

  // 开始编辑 meta
  const startEditMeta = useCallback((node: OutlineItem) => {
    setEditingMetaId(node.id);
    setEditingMetaTitle(node.title || '');
    setEditDescription(node.description || '');
    setEditWordCountRange(node.word_count_range || '');
  }, []);

  // 保存 meta
  const saveEditMeta = useCallback(() => {
    if (!editingMetaId) return;
    setLocalOutline(prev => updateNodeInTree(prev, editingMetaId, (node) => ({
      ...node,
      description: editDescription,
      word_count_range: editWordCountRange,
    })));
    setEditingMetaId(null);
    setEditingMetaTitle('');
  }, [editingMetaId, editDescription, editWordCountRange, updateNodeInTree]);

  // 取消编辑 meta
  const cancelEditMeta = useCallback(() => {
    setEditingMetaId(null);
    setEditingMetaTitle('');
    setAiGeneratingDesc(false);
  }, []);

  // AI 生成描述
  const handleAiGenerateDescription = useCallback(async () => {
    if (!editingMetaTitle || !projectId || aiGeneratingDesc) return;
    setAiGeneratingDesc(true);
    try {
      const response = await projectApi.generateNodeDescription(projectId, editingMetaTitle);
      if (response.data.description) {
        setEditDescription(response.data.description);
      }
    } catch (err) {
      // 静默失败，用户可在 UI 看到错误
      console.warn('AI 生成描述失败:', err);
    } finally {
      setAiGeneratingDesc(false);
    }
  }, [editingMetaTitle, projectId, aiGeneratingDesc]);

  // ==================== 拖拽排序 ====================

  const handleNodeDragStart = useCallback((e: React.DragEvent, item: OutlineItem) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    dragNodeRef.current = item;
    requestAnimationFrame(() => {
      setDragNodeId(item.id);
    });
  }, []);

  const handleNodeDragEnd = useCallback(() => {
    setDragNodeId(null);
    setDropTarget(null);
    dragNodeRef.current = null;
  }, []);

  const handleNodeDragOver = useCallback((e: React.DragEvent, item: OutlineItem) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (!dragNodeRef.current || dragNodeRef.current.id === item.id) {
      setDropTarget(null);
      return;
    }

    if (isDescendantOf(localOutline, item.id, dragNodeRef.current.id)) {
      setDropTarget(null);
      return;
    }

    const contentEl = (e.currentTarget as HTMLElement).querySelector<HTMLElement>('[draggable="true"]');
    const rect = (contentEl || e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;

    let position: 'before' | 'inside' | 'after';
    if (y < h * 0.3) {
      position = 'before';
    } else if (y > h * 0.7) {
      position = 'after';
    } else {
      position = 'inside';
    }

    setDropTarget({ id: item.id, position });
  }, [localOutline]);

  const handleNodeDragLeave = useCallback((e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !(e.currentTarget as HTMLElement).contains(relatedTarget)) {
      setDropTarget(null);
    }
  }, []);

  const handleNodeDrop = useCallback((e: React.DragEvent, targetItem: OutlineItem) => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragNodeRef.current || !dropTarget) return;
    if (dragNodeRef.current.id === targetItem.id) return;

    if (isDescendantOf(localOutline, targetItem.id, dragNodeRef.current.id)) {
      handleNodeDragEnd();
      return;
    }

    const draggedItem = { ...dragNodeRef.current };

    // 从原位置移除
    const { items: afterRemove } = removeItemFromTree(localOutline, draggedItem.id);

    // 插入到目标位置
    const newTree = insertItemToTree(afterRemove, draggedItem, dropTarget.id, dropTarget.position);

    // 重新编号
    const renumberedTree = renumberTree(newTree);

    setLocalOutline(renumberedTree);
    handleNodeDragEnd();
  }, [localOutline, dropTarget, handleNodeDragEnd]);

  // ==================== 保存 ====================

  const handleSave = useCallback(async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const renumbered = renumberTree(localOutline);
      setLocalOutline(renumbered);
      const nodes = flattenNodes(renumbered, [], null, section);
      await projectApi.saveOutline(projectId, nodes);

      // 通知父组件更新
      if (onOutlineUpdated && outlineData) {
        onOutlineUpdated({
          ...outlineData,
          outline: renumbered,
        });
      }
      onClose();
    } catch (err: any) {
      alert('保存失败: ' + (err?.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  }, [projectId, localOutline, outlineData, onOutlineUpdated, onClose, section]);

  // ==================== 折叠/展开 ====================

  const toggleCollapse = useCallback((nodeId: string) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // 收集所有父节点 ID
  const collectParentIds = useCallback((items: OutlineItem[]): string[] => {
    const ids: string[] = [];
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        ids.push(item.id);
        ids.push(...collectParentIds(item.children));
      }
    }
    return ids;
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(() => {
    setCollapsedIds(new Set(collectParentIds(localOutline)));
  }, [localOutline, collectParentIds]);

  // ==================== 渲染目录树 ====================

  const renderTree = useCallback((items: OutlineItem[], level: number = 0): React.ReactElement[] => {
    return items.map(item => {
      const isLeaf = !item.children || item.children.length === 0;
      const isDraggedNode = dragNodeId === item.id;
      const isDropBefore = dropTarget?.id === item.id && dropTarget?.position === 'before';
      const isDropInside = dropTarget?.id === item.id && dropTarget?.position === 'inside';
      const isDropAfter = dropTarget?.id === item.id && dropTarget?.position === 'after';

      return (
        <div
          key={item.id}
          data-chapter-id={item.id}
          onDragOver={readOnly ? undefined : (e) => handleNodeDragOver(e, item)}
          onDragLeave={readOnly ? undefined : handleNodeDragLeave}
          onDrop={readOnly ? undefined : (e) => handleNodeDrop(e, item)}
        >
          {isDropBefore && (
            <div className="h-0.5 bg-blue-500 rounded-full mx-1" style={{ marginLeft: `${level * 20 + 12}px` }} />
          )}
          <div
            draggable={!readOnly}
            onDragStart={readOnly ? undefined : (e) => handleNodeDragStart(e, item)}
            onDragEnd={readOnly ? undefined : handleNodeDragEnd}
            className={`group flex items-center gap-1.5 py-1.5 px-2 rounded-md transition-colors ${
              isDraggedNode ? 'opacity-40' : ''
            } ${
              isDropInside ? 'bg-blue-100 border border-blue-300 border-dashed' : 'hover:bg-gray-50'
            }`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
          >
            {/* 折叠/展开箭头（仅父节点显示） */}
            {!isLeaf ? (
              <button
                onClick={e => { e.stopPropagation(); toggleCollapse(item.id); }}
                className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title={collapsedIds.has(item.id) ? '展开子节点' : '折叠子节点'}
              >
                {collapsedIds.has(item.id) ? (
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                )}
              </button>
            ) : (
              <span className="w-4 flex-shrink-0" />
            )}

            {/* 标题 - 可编辑 */}
            {editingTitleId === item.id ? (
              <div className="flex-1 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <input
                  ref={titleInputRef}
                  value={editTitleText}
                  onChange={e => setEditTitleText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveEditTitle();
                    if (e.key === 'Escape') cancelEditTitle();
                  }}
                  onBlur={saveEditTitle}
                  className="flex-1 text-sm px-2 py-0.5 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  autoFocus
                />
                <button onClick={saveEditTitle} className="p-0.5 text-green-500 hover:bg-green-50 rounded">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={cancelEditTitle} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded">
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <span
                className={`text-sm flex-1 truncate cursor-pointer ${
                  isLeaf ? 'text-gray-700' : 'text-gray-600 font-medium'
                }`}
                onDoubleClick={readOnly ? undefined : () => startEditTitle(item)}
                title={readOnly ? '' : "双击编辑标题"}
              >
                {item.title}
              </span>
            )}

            {/* 描述预览 */}
            {item.description && editingMetaId !== item.id && editingTitleId !== item.id && (
              <span
                className={`text-xs text-gray-400 max-w-[500px] line-clamp-3 ${
                  showDescriptions ? 'inline' : 'hidden'
                }`}
                title={item.description}
              >
                {item.description}
              </span>
            )}
            {item.word_count_range && editingMetaId !== item.id && editingTitleId !== item.id && (
              <span className="text-[10px] text-purple-400 bg-purple-50 px-1 rounded flex-shrink-0">
                {item.word_count_range}
              </span>
            )}

            {/* 节点 ID（右侧） */}
            <span className="text-[10px] text-gray-300 flex-shrink-0 ml-1 truncate max-w-[80px]" title={item.id}>
              {item.id}
            </span>

            {/* 操作按钮（hover 显示） */}
            {!readOnly && (
            <div className="flex-shrink-0 flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* 编辑标题 */}
              <button
                onClick={e => { e.stopPropagation(); startEditTitle(item); }}
                className="p-0.5 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50"
                title="编辑标题"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
              </button>
              {/* 编辑描述/字数 */}
              <button
                onClick={e => { e.stopPropagation(); startEditMeta(item); }}
                className="p-0.5 rounded text-gray-400 hover:text-purple-500 hover:bg-purple-50"
                title="编辑描述/字数范围"
              >
                <DocumentTextIcon className="w-3.5 h-3.5" />
              </button>
              {/* 添加子节点 */}
              <button
                onClick={e => { e.stopPropagation(); handleAddChild(item.id); }}
                className="p-0.5 rounded text-gray-400 hover:text-green-500 hover:bg-green-50"
                title="添加子节点"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                <span className="text-[8px] ml-0.5">子</span>
              </button>
              {/* 添加同级节点 */}
              <button
                onClick={e => { e.stopPropagation(); handleAddSibling(item.id); }}
                className="p-0.5 rounded text-gray-400 hover:text-green-500 hover:bg-green-50"
                title="添加同级节点"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                <span className="text-[8px] ml-0.5">同</span>
              </button>
              {/* 删除 */}
              <button
                onClick={e => { e.stopPropagation(); handleDeleteClick(item); }}
                className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                title="删除节点"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            )}
          </div>

          {/* Meta 编辑区 */}
          {editingMetaId === item.id && (
            <div
              className="ml-8 mr-2 mb-1 p-2 bg-yellow-50 rounded border border-yellow-200"
              style={{ marginLeft: `${level * 20 + 12}px` }}
            >
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500">描述</label>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAiGenerateDescription(); }}
                  disabled={aiGeneratingDesc}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title="AI 根据标题自动生成描述"
                >
                  {aiGeneratingDesc ? (
                    <><ArrowPathIcon className="w-3 h-3 animate-spin" /> 生成中...</>
                  ) : (
                    <><SparklesIcon className="w-3 h-3" /> AI 生成</>
                  )}
                </button>
              </div>
              <div className="flex gap-2 mb-2">
                <textarea
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  placeholder="章节描述"
                  rows={3}
                  className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-yellow-400 resize-y"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-16 flex-shrink-0">字数</label>
                <input
                  value={editWordCountRange}
                  onChange={e => setEditWordCountRange(e.target.value)}
                  placeholder="如 800-1500"
                  className="w-32 text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-yellow-400"
                />
                <div className="flex-1" />
                <button
                  onClick={saveEditMeta}
                  className="px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded"
                >
                  保存
                </button>
                <button
                  onClick={cancelEditMeta}
                  className="px-2 py-1 text-xs text-gray-400 hover:bg-gray-100 rounded"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          {/* 拖拽指示线 - after */}
          {isDropAfter && (
            <div className="h-0.5 bg-blue-500 rounded-full mx-1" style={{ marginLeft: `${level * 20 + 12}px` }} />
          )}

          {/* 子节点 */}
          {item.children && item.children.length > 0 && !collapsedIds.has(item.id) && (
            <>{renderTree(item.children, level + 1)}</>
          )}
        </div>
      );
    });
  }, [
    dragNodeId, dropTarget, editingTitleId, editingMetaId,
    editTitleText, editDescription, editWordCountRange,
    showDescriptions, collapsedIds, toggleCollapse,
    handleNodeDragOver, handleNodeDragLeave, handleNodeDrop,
    handleNodeDragStart, handleNodeDragEnd,
    saveEditTitle, cancelEditTitle, startEditTitle, startEditMeta,
    saveEditMeta, cancelEditMeta, handleAddChild, handleAddSibling, handleDeleteClick,
  ]);

  // ==================== 主渲染 ====================

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* 遮罩 */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* 面板 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-[97vw] h-[92vh] max-w-[1600px] flex flex-col z-10">
        {/* 头部 */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">目录结构全屏编辑</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              拖动节点排序 · 双击编辑标题 · 悬浮按钮操作增删
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* 显示描述开关 */}
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <span className="text-xs text-gray-500">描述</span>
              <button
                type="button"
                role="switch"
                aria-checked={showDescriptions}
                onClick={() => setShowDescriptions(v => !v)}
                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
                  showDescriptions ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    showDescriptions ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </label>
            {/* 展开/折叠 */}
            <button
              onClick={expandAll}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="全部展开"
            >
              <ChevronDownIcon className="w-3.5 h-3.5 inline mr-0.5" />
              展开
            </button>
            <button
              onClick={collapseAll}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="全部折叠"
            >
              <ChevronRightIcon className="w-3.5 h-3.5 inline mr-0.5" />
              折叠
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !initialized || readOnly}
              title={readOnly ? '只读模式，无法保存' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? '保存中...' : '保存'}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto p-4">
          {!initialized ? (
            <div className="flex items-center justify-center h-full text-gray-400">加载中...</div>
          ) : localOutline.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <DocumentTextIcon className="w-12 h-12 mb-2" />
              <p className="text-sm">暂无目录结构</p>
            </div>
          ) : (
            <div className="space-y-0.5">{renderTree(localOutline)}</div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="flex-shrink-0 px-6 py-2 border-t border-gray-100 text-xs text-gray-400 flex items-center justify-between">
          <span>提示：点击 <span className="text-blue-500 font-medium">保存</span> 后修改生效，关闭窗口将丢弃修改</span>
        </div>
      </div>

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title="确认删除节点？"
        confirmText="删除"
        cancelText="取消"
        confirmClassName="bg-red-600 hover:bg-red-700"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      >
        <p className="text-gray-600">
          将删除节点 <strong>「{deleteConfirm?.nodeTitle}」</strong>
        </p>
        {deleteConfirm?.hasChildren && (
          <p className="mt-1 text-orange-500 font-medium text-sm">
            ⚠ 该节点包含子节点，删除后将一并移除所有子节点！
          </p>
        )}
        {deleteConfirm?.hasContent && (
          <p className="mt-1 text-red-500 font-medium text-sm">
            ⚠ 该节点已生成内容，删除后内容将丢失！
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default OutlineEditorDialog;
